import { makeStyles } from '@material-ui/styles'
import { indexBy, path, pluck, prop } from 'ramda'
import React, {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useReactRouter from 'use-react-router'
import type { ArrayElement } from 'core/actions/Action'
import type DeleteAction from 'core/actions/DeleteAction'
import CreateButton from 'core/components/buttons/CreateButton'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import ViewModeToggle, { ViewMode } from 'core/components/ViewModeToggle'
import Breadcrumbs from 'core/elements/breadcrumbs'
import Button from 'core/elements/button'
import Grid from 'core/elements/grid'
import GridDefaultActionButton from 'core/elements/grid/buttons/GridDefaultActionButton'
import GridDefaultDeleteButton from 'core/elements/grid/buttons/GridDefaultDeleteButton'
import type { GridProps } from 'core/elements/grid/Grid'
import GridSearchFilter from 'core/elements/grid/GridSearchFilter'
import useGridFiltering, {
  type GridGlobalFilterSpec,
} from 'core/elements/grid/hooks/useGridFiltering'
import type {
  BatchActionButtonProps,
  GridBatchActionSpec,
} from 'core/elements/grid/hooks/useGridSelectableRows'
import {
  HeaderPrimaryActionPortal,
  HeaderRefreshPortal,
  HeaderTitlePortal,
} from 'core/elements/header/portals'
import Tooltip from 'core/elements/tooltip'
import useToggler from 'core/hooks/useToggler'
import useUpdateAction from 'core/hooks/useUpdateAction'
import type Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import type DataKeys from 'k8s/DataKeys'
import { entityNamesByKey } from 'k8s/DataKeys'
import type { IDataKeys } from 'k8s/datakeys.model'
import { bypassKey, hasPermissions } from 'openstack/helpers'
import { emptyArr, isNilOrEmpty } from 'utils/fp'
import { humanizeFieldName, pathJoin } from 'utils/misc'
import { trackEvent } from 'utils/tracking'
import CardViewContainer from './CardViewContainer'

interface SearchFunctionProps<T> {
  property: string
  searchFn: (value: keyof T | string, searchString: string, item?: any) => boolean
}

export interface ListContainerProps<D extends keyof IDataKeys, T = ArrayElement<IDataKeys[D]>>
  extends GridProps<T> {
  dataKey?: DataKeys
  searchTargets: Array<keyof T | string>
  searchTargetLabels?: Record<string, string>
  searchFunctions?: SearchFunctionProps<T>[]
  searchBoxLabel?: string
  nameProp?: keyof T
  disableSearch?: boolean
  initialSearchValue?: string

  addText?: string
  addUrl?: string | ((selectedItem: T, id: string) => string)
  addCond?: () => boolean
  AddButtonComponent?: FC<BatchActionButtonProps<T>>
  AddDialogComponent?: FC<
    PropsWithChildren<{
      onClose: () => void
    }>
  >
  disableAddButton?: boolean
  disabledAddButtonInfo?: string

  editText?: string
  editUrl?: string | ((selectedItem: T, id: string) => string)
  editCond?: (selectedItems: T[]) => boolean
  editDisabledInfo?: string
  EditButtonComponent?: FC<BatchActionButtonProps<T>>
  EditDialogComponent?: FC<
    PropsWithChildren<{
      onClose: () => void
      rows: T[]
    }>
  >

  deleteText?: string
  deleteAction?: DeleteAction<D>
  deleteCond?: (selectedItems: T[]) => boolean
  deleteDisabledInfo?: string
  DeleteButtonComponent?: FC<BatchActionButtonProps<T>>
  DeleteDialogComponent?: FC<
    PropsWithChildren<{
      onClose: () => void
      rows: T[]
      onSuccess?: () => void
    }>
  >
  postDeleteAction?: () => void
  showBreadcrumbs?: boolean
  extraHeaderContent?: ReactNode
  extraToolbarContent?: ReactNode
  label?: string
  getParamsUpdater?: (...keys: string[]) => (...values: unknown[]) => void
  tooltip?: ReactNode
  dropdownFilters?: any[]
  isReadOnly?: boolean
  hideCheckboxes?: boolean
  iconOnly?: boolean
  hideRefreshButtonOnTop?: boolean
  CardComponent?: FC<
    PropsWithChildren<{
      item: any
    }>
  >
  HeaderActions?: ReactNode
  refreshing?: boolean
  hideHeaderPortals?: boolean
  useFloatingActionBar?: boolean
  hideLastUpdatedTimeStamp?: boolean
  listPermsKeyPath?: string[]
  createPermsKeyPath?: string[]
  editPermsKeyPath?: string[]
  deletePermsKeyPath?: string[]
}

// Module-level constant avoids new array ref on each render when no custom filters provided
const EMPTY_GLOBAL_FILTERS = [] as const

export const getDeleteConfirmText = (selectedItems, nameProp = 'name') => {
  if (isNilOrEmpty(selectedItems)) {
    return ''
  }
  const pluckName = pluck(nameProp)
  const selectedNames = pluckName(selectedItems).join(', ')
  return `This will permanently delete the following: ${selectedNames}`
}

export default function ListContainer<D extends keyof IDataKeys, T = ArrayElement<IDataKeys[D]>>(
  props: ListContainerProps<D, T>,
) {
  const {
    dataKey,
    uniqueIdentifier,
    searchTargets,
    searchTargetLabels,
    searchFunctions,
    searchBoxLabel,
    nameProp = 'name',
    disableSearch = false,
    initialSearchValue,
    batchActions = [],
    dropdownBatchActions = [],
    globalFilters: customGlobalFilters = EMPTY_GLOBAL_FILTERS,
    dropdownFilters = [],

    addText = 'Add',
    AddButtonComponent = CreateButton,
    addUrl,
    addCond,
    AddDialogComponent,
    disableAddButton = false,
    disabledAddButtonInfo = '',

    editText = 'Edit',
    editDisabledInfo,
    EditButtonComponent = GridDefaultActionButton,
    EditDialogComponent,
    editUrl,
    editCond,

    deleteText = 'Delete',
    deleteAction,
    deleteCond,
    deleteDisabledInfo,
    DeleteButtonComponent = GridDefaultDeleteButton,
    DeleteDialogComponent,
    postDeleteAction,

    showBreadcrumbs = true,
    extraHeaderContent,
    loading,
    label = entityNamesByKey[dataKey],

    getParamsUpdater,
    hideCheckboxes = false,
    // By Default isReadOnly is set to true if the user is readonly and you can override it using the isReadOnly prop
    isReadOnly = isReadonlyUser(),
    disableRowSelection = false,
    multiSelection,
    onRefresh,
    iconOnly,
    hideRefreshButtonOnTop = true,
    visibleColumns,
    columnsOrder,

    data,
    CardComponent,
    HeaderActions,
    refreshing,
    hideHeaderPortals = false,
    useFloatingActionBar = true,
    hideLastUpdatedTimeStamp = false,
    listPermsKeyPath = [bypassKey],
    createPermsKeyPath = [bypassKey],
    editPermsKeyPath = [bypassKey],
    deletePermsKeyPath = [bypassKey],
  } = props
  const classes = useStyles()
  const url = new URL(window.location.href)
  const selectedView = url.searchParams.get('view') as ViewMode | null

  const { history } = useReactRouter()
  const [showingConfirmDialog, toggleConfirmDialog] = useToggler()
  const [showingEditDialog, toggleEditDialog] = useToggler()
  const [showingAddDialog, toggleAddDialog] = useToggler()
  const [selectedItems, setSelectedItems] = useState<T[]>(emptyArr)
  const [view, setView] = useState<ViewMode>(selectedView || ViewMode.LIST)
  const [filteredRows, setFilteredRows] = useState([])
  const [selectedSearchField, setSelectedSearchField] = useState<string>('all')

  const { update: handleRemove, updating: deleting } = deleteAction
    ? useUpdateAction(deleteAction)
    : { update: null, updating: false }

  const searchFieldOptions = useMemo(
    () =>
      (searchTargets ?? []).map((target) => ({
        value: String(target),
        label: searchTargetLabels?.[String(target)] || humanizeFieldName(String(target)),
      })),
    [searchTargets, searchTargetLabels],
  )

  const handleSearchFieldChange = useCallback((field: string) => {
    setSelectedSearchField(field)
  }, [])

  // @ts-ignore
  const globalFilters = useMemo<Array<GridGlobalFilterSpec<T, Record<string, unknown>>>>(
    () => [
      ...(!disableSearch
        ? [
            {
              key: 'search',
              label: searchBoxLabel,
              equalityComparerFn: (item, value) => {
                const searchFunctionsByProperty = searchFunctions
                  ? indexBy(prop('property'), searchFunctions)
                  : {}
                const allFilteredTargets = searchTargets?.filter((target: string) => {
                  const targetBase = target?.split('.')[0]
                  // This used to use visibleColumns, but that
                  // was not updated immediately when columns were
                  // changed, only on loading the list table
                  return columnsOrder?.includes(targetBase)
                })
                const filteredSearchTargets =
                  selectedSearchField !== 'all'
                    ? allFilteredTargets?.filter((t) => String(t) === selectedSearchField)
                    : allFilteredTargets
                return !!filteredSearchTargets?.find((key) => {
                  // @ts-ignore
                  if (searchFunctions && searchFunctionsByProperty[key]) {
                    // @ts-ignore
                    const searchFn = searchFunctionsByProperty[key]?.searchFn
                    return searchFn(item[String(key)], value, item)
                  }
                  return String(
                    Object.hasOwn(item as object, key)
                      ? item[String(key)]
                      : path(String(key).split('.'), item),
                  )
                    .toLocaleLowerCase()
                    .includes(value.toLocaleLowerCase())
                })
              },
              FilterComponent: GridSearchFilter,
              initialValue: initialSearchValue || undefined,
              searchFields: searchFieldOptions,
              onFieldChange: handleSearchFieldChange,
            } as GridGlobalFilterSpec<T, Record<string, unknown>, 'search', string>,
          ]
        : []),
      ...customGlobalFilters,
    ],
    [
      searchTargets,
      searchFunctions,
      columnsOrder,
      searchBoxLabel,
      disableSearch,
      initialSearchValue,
      selectedSearchField,
      searchFieldOptions,
      handleSearchFieldChange,
      customGlobalFilters,
    ],
  )

  const deleteConfirmText = useMemo(
    () => getDeleteConfirmText(selectedItems, String(nameProp)),
    [selectedItems],
  )

  const hasListPermissions = hasPermissions(listPermsKeyPath)
  const hasCreatePermissions = hasPermissions(createPermsKeyPath)
  const hasEditPermissions = hasPermissions(editPermsKeyPath)
  const hasDeletePermissions = hasPermissions(deletePermsKeyPath)

  const allBatchActions = useMemo(() => {
    const defaultBatchActions: GridBatchActionSpec<T>[] = []
    const deleteBatchAction: GridBatchActionSpec<T>[] = []

    if ((EditDialogComponent || editUrl) && hasEditPermissions) {
      defaultBatchActions.push({
        label: editText,
        cond: (selectedItems) => {
          return editCond ? editCond(selectedItems) : selectedItems.length === 1
        },
        disabledTooltip: (selectedItems) => {
          if (editDisabledInfo) {
            return editDisabledInfo
          }
          if (selectedItems.length > 1) {
            return 'You can only edit one item at a time'
          }
          return ''
        },
        handleAction: async (selectedItems) => {
          if (editUrl) {
            const [selectedRow] = selectedItems
            const selectedId = String(selectedRow[uniqueIdentifier])
            const urlResult =
              typeof editUrl === 'function'
                ? editUrl(selectedRow, selectedId)
                : pathJoin(editUrl, selectedId)
            history.push(urlResult)
            return
          }
          setSelectedItems(selectedItems)
          toggleEditDialog()
        },
        BatchActionButton: EditButtonComponent,
        iconOnly,
        tooltip: iconOnly ? 'Edit' : null,
      })
    }

    if (handleRemove && hasDeletePermissions) {
      deleteBatchAction.push({
        label: deleteText,
        cond: deleteCond,
        disabledTooltip: deleteDisabledInfo,
        keepRowsSelected: false,
        handleAction: async (selectedItems) => {
          setSelectedItems(selectedItems)
          toggleConfirmDialog()
        },
        BatchActionButton: DeleteButtonComponent,
        iconOnly,
        tooltip: iconOnly ? 'Delete' : null,
        refreshAfterSuccess: false,
      })
    }

    return [...defaultBatchActions, ...batchActions, ...deleteBatchAction]
  }, [uniqueIdentifier, handleRemove, batchActions, hasEditPermissions, hasDeletePermissions])

  const handleDeleteConfirm = useCallback(async () => {
    toggleConfirmDialog()
    await Promise.all(selectedItems?.map(handleRemove))
    // Run post delete action if provided
    postDeleteAction?.()
  }, [selectedItems, handleRemove])

  const handleAdd = () => {
    trackEvent(`Clicked ${addText}`)
    if (addUrl) {
      history.push(addUrl)
    } else if (AddDialogComponent) {
      toggleAddDialog()
    }
  }

  const addEnabled = useMemo(() => {
    // eslint-disable-next-line no-extra-boolean-cast
    const didAddCondPass = addCond ? addCond() : true
    const isCustomAddButton = AddButtonComponent !== CreateButton
    return (
      (!!AddDialogComponent || !!addUrl || isCustomAddButton) &&
      didAddCondPass &&
      !isReadOnly &&
      hasCreatePermissions
    )
  }, [addCond, isReadOnly, AddButtonComponent, AddDialogComponent, addUrl, hasCreatePermissions])

  const shouldRenderCardView = useMemo(
    () => CardComponent && view === ViewMode.CARD,
    [CardComponent, view],
  )

  return (
    <>
      {showBreadcrumbs && (
        <HeaderTitlePortal>
          <Breadcrumbs />
        </HeaderTitlePortal>
      )}
      {AddDialogComponent && showingAddDialog && hasCreatePermissions && (
        <AddDialogComponent onClose={toggleAddDialog}>{addText}</AddDialogComponent>
      )}
      {EditDialogComponent && showingEditDialog && hasEditPermissions && (
        <EditDialogComponent onClose={toggleEditDialog} rows={selectedItems}>
          {editText}
        </EditDialogComponent>
      )}
      {DeleteDialogComponent && showingConfirmDialog && hasDeletePermissions && (
        <DeleteDialogComponent
          onClose={toggleConfirmDialog}
          rows={selectedItems}
          onSuccess={postDeleteAction ? postDeleteAction : undefined}
        >
          {deleteText}
        </DeleteDialogComponent>
      )}
      {!DeleteDialogComponent && handleRemove && showingConfirmDialog && hasDeletePermissions && (
        <ConfirmationDialog
          open={showingConfirmDialog}
          text={deleteConfirmText}
          onCancel={toggleConfirmDialog}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {onRefresh && !hideRefreshButtonOnTop ? (
        <HeaderRefreshPortal>
          <Button variant="secondary" onClick={onRefresh} icon="sync">
            Refresh
          </Button>
        </HeaderRefreshPortal>
      ) : null}
      <HeaderPrimaryActionPortal>
        {extraHeaderContent}
        {addEnabled && (
          <Tooltip message={disableAddButton ? disabledAddButtonInfo : ''}>
            <AddButtonComponent disabled={disableAddButton} onClick={handleAdd}>
              {addText}
            </AddButtonComponent>
          </Tooltip>
        )}
      </HeaderPrimaryActionPortal>

      {hideHeaderPortals ? null : (
        <div className={classes.headerPortals}>
          <div className={classes.viewModeToggleContainer}>
            {CardComponent && <ViewModeToggle view={view} onViewChange={setView} />}
            {shouldRenderCardView && (
              <SearchComponentForCardView
                data={data}
                globalFilters={globalFilters}
                uniqueIdentifier={uniqueIdentifier}
                setFilteredRows={setFilteredRows}
              />
            )}
          </div>
          {HeaderActions && <div className={classes.headerActions}>{HeaderActions}</div>}
        </div>
      )}

      {shouldRenderCardView ? (
        <CardViewContainer
          data={hasListPermissions ? filteredRows : []}
          CardComponent={CardComponent}
          uniqueIdentifier={uniqueIdentifier}
          emptyText={
            hasListPermissions ? null : 'You do not have the permissions to view this resource'
          }
        />
      ) : hasListPermissions ? (
        <Grid<T>
          {...props}
          multiSelection={!isReadOnly ? multiSelection : false}
          isReadOnly={isReadOnly}
          label={label}
          globalFilters={globalFilters}
          dropdownFilters={dropdownFilters}
          batchActions={allBatchActions}
          dropdownBatchActions={dropdownBatchActions}
          loading={loading || deleting}
          onSortChange={
            props.onSortChange ||
            (getParamsUpdater && getParamsUpdater('orderBy', 'orderDirection'))
          }
          onRowsPerPageChange={getParamsUpdater && getParamsUpdater('rowsPerPage')}
          onColumnsChange={getParamsUpdater && getParamsUpdater('visibleColumns', 'columnsOrder')}
          hideCheckboxes={hideCheckboxes}
          disableRowSelection={disableRowSelection || isReadOnly}
          isBackgroundRefresh={!(loading || deleting) && refreshing}
          useFloatingActionBar={useFloatingActionBar}
        />
      ) : (
        <Grid
          uniqueIdentifier="notAllowed"
          data={[]}
          columns={[]}
          label={label}
          emptyContent="You do not have the permissions to view this resource"
        />
      )}
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  headerPortals: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  viewModeToggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  itemsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterComponent: {
    background: theme.palette.common.white,
    padding: '2px 120px 2px 8px',
    border: `1px solid ${theme.palette.grey[200]}`,
    '& input': {
      width: '210px',
    },
  },
}))

/**
 * @summary Search component for card view
 * @param
 */
const SearchComponentForCardView = <T extends Record<string, any>>({
  data,
  globalFilters,
  uniqueIdentifier,
  setFilteredRows,
}: {
  data: T[]
  globalFilters: any[]
  uniqueIdentifier: string | number | symbol
  setFilteredRows: (rows: Array<any>) => void
}) => {
  const classes = useStyles()
  const rows = data.map((item) => ({
    item,
    key: String(item[uniqueIdentifier as string]),
    getCells: () => Object.values(item),
  }))
  const [filteredRows, filteringProps] = useGridFiltering(rows, {
    globalFilters,
  })

  // Update filtered rows when filteredRows changes
  useEffect(() => {
    setFilteredRows(filteredRows)
  }, [filteredRows])

  return (
    <>
      {filteringProps?.globalFilters.map(
        ({ key, filterValue, filterValues, updateFilterValue, FilterComponent, ...rest }) => (
          <div className={classes.filterComponent}>
            <FilterComponent
              key={String(key)}
              value={filterValue}
              filterValues={filterValues}
              onChange={updateFilterValue}
              {...rest}
            />
          </div>
        ),
      )}
    </>
  )
}
